import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../firebaseConfig";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { motion } from "framer-motion";

const RecurringEventsContainer = ({ jobData }) => {
  const [theme] = useAtom(themeAtom);
  const [recurringEvent, setRecurringEvent] = useState(false);
  const [rrule, setRrule] = useState({
    byweekday: [],
    freq: "weekly",
    interval: 1,
    dtstart: null,
    until: null,
  });
  const [exDate, setExDate] = useState([]);

  useEffect(() => {
    setRecurringEvent(jobData.recurringEvent || false);
    setRrule(jobData.rrule || {
      byweekday: [],
      freq: "weekly", 
      interval: 1,
      dtstart: null,
      until: null,
    });
    setExDate(jobData.exdate || []);
  }, [jobData]);

  const updateFSNow = (field, value) => {
    // Format and validate rrule data before saving
    let processedValue = value;
    if (field === "rrule" && value) {
      processedValue = formatRRuleDates(value);
      if (!validateRRule(processedValue)) {
        console.error("Invalid rrule data:", processedValue);
        return;
      }
    }
    
    updateDoc(doc(db, "JobData", jobData.id), { [field]: processedValue })
      .catch((error) => {
        console.log(error);
      });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "8px 12px",
    minWidth: { xs: "auto", sm: "50px" },
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: { xs: 10, sm: 11 },
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={recurringEvent || false}
                    onChange={(e) => {
                      setRecurringEvent(e.target.checked);
                      updateFSNow("recurringEvent", e.target.checked);
                      
                      if (e.target.checked) {
                        // Initialize rrule when enabling recurring event
                        const currentDate = new Date();
                        const futureDate = new Date();
                        futureDate.setFullYear(currentDate.getFullYear() + 20);
                        
                        const initialRrule = {
                          byweekday: [],
                          freq: "weekly", // Store as string for FullCalendar compatibility
                          interval: 1,
                          dtstart: jobData.bookedStart || new Date().toISOString(),
                          until: futureDate.toISOString(),
                        };
                        setRrule(initialRrule);
                        updateFSNow("rrule", initialRrule);
                      }
                    }}
                    sx={{
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.7)"
                          : "rgba(0,0,0,0.7)",
                      "&.Mui-checked": {
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                      },
                    }}
                  />
                }
                label="Recurring Event"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color:
                      theme === "dark"
                        ? "rgba(255,255,255,0.9)"
                        : "rgba(0,0,0,0.9)",
                    fontWeight: 600,
                  },
                }}
              />
            </FormGroup>
          </motion.div>

          {recurringEvent && (
            <motion.div variants={itemVariants}>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <FormControl sx={{ width: 200 }}>
                    <InputLabel 
                      id="freq-selector"
                      sx={{
                        color:
                          theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                      }}
                    >
                      Frequency
                    </InputLabel>
                    <Select
                      labelId="freq-selector-select"
                      value={rrule?.freq || "weekly"}
                      label="Frequency"
                      variant="filled"
                      disableUnderline
                      onChange={(e) => {
                        const newRrule = { ...rrule, freq: e.target.value };
                        setRrule(newRrule);
                        updateFSNow("rrule", newRrule);
                      }}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        boxShadow:
                          theme === "dark"
                            ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                            : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      }}
                    >
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <span
                    style={{
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(0,0,0,0.9)",
                      fontWeight: 500,
                    }}
                  >
                    Every
                  </span>
                  <TextField
                    type="number"
                    value={rrule?.interval || 1}
                    onChange={(e) => {
                      const newRrule = { ...rrule, interval: parseInt(e.target.value) || 1 };
                      setRrule(newRrule);
                      updateFSNow("rrule", newRrule);
                    }}
                    variant="filled"
                    size="small"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                        sx: {
                          borderRadius: "8px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(45,45,45,0.7)"
                              : "rgba(250,250,250,0.9)",
                          width: 60,
                        },
                      },
                    }}
                  />
                  <span
                    style={{
                      color:
                        theme === "dark"
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(0,0,0,0.9)",
                      fontWeight: 500,
                    }}
                  >
                    {rrule?.freq?.replace("ly", "")}(s) on:
                  </span>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <ToggleButtonGroup
                    value={rrule?.byweekday || []}
                    onChange={(event, newValue) => {
                      // Filter out null values and ensure we have an array
                      const cleanValue = newValue ? newValue.filter(v => v !== null) : [];
                      const newRrule = { ...rrule, byweekday: cleanValue, bymonth: [] };
                      setRrule(newRrule);
                      updateFSNow("rrule", newRrule);
                    }}
                    sx={{
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: 1,
                    }}
                  >
                    <ToggleButton
                      value="mo"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      MON
                    </ToggleButton>
                    <ToggleButton
                      value="tu"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      TUE
                    </ToggleButton>
                    <ToggleButton
                      value="we"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      WED
                    </ToggleButton>
                    <ToggleButton
                      value="th"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      THU
                    </ToggleButton>
                    <ToggleButton
                      value="fr"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      FRI
                    </ToggleButton>
                    <ToggleButton
                      value="sa"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      SAT
                    </ToggleButton>
                    <ToggleButton
                      value="su"
                      size="small"
                      sx={filterButtonStyles}
                      component={motion.button}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      SUN
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Button
                  variant="outlined"
                  onClick={() => {
                    if (jobData.bookedStart) {
                      const date = new Date(jobData.bookedStart);
                      const year = date.getFullYear();
                      let month = date.getMonth() + 1;
                      if (month < 10) month = `0${month}`;
                      let day = date.getDate();
                      if (day < 10) day = `0${day}`;

                      const exDateStr = `${year}-${month}-${day}`;
                      // Check if date is already excluded
                      if (!exDate.includes(exDateStr)) {
                        const newExDate = [...exDate, exDateStr];
                        setExDate(newExDate);
                        updateFSNow("exdate", newExDate);
                      }
                    }
                  }}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor:
                      theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                    color:
                      theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
                    "&:hover": {
                      borderColor:
                        theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                      backgroundColor:
                        theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  Exclude This Date
                </Button>
              </Box>
            </motion.div>
          )}
        </motion.div>
      </Box>
    </FormControl>
  );
};

export default RecurringEventsContainer;

// Helper functions to validate rrule data
const validateRRule = (rrule) => {
  if (!rrule) return false;
  
  // Ensure required fields exist
  if (!rrule.freq || !rrule.interval) return false;
  
  // Validate frequency
  const validFreqs = ["weekly", "monthly", "yearly"];
  if (!validFreqs.includes(rrule.freq)) return false;
  
  // Validate interval
  if (typeof rrule.interval !== "number" || rrule.interval < 1) return false;
  
  // Validate byweekday if it exists
  if (rrule.byweekday && !Array.isArray(rrule.byweekday)) return false;
  
  return true;
};

// Helper to ensure rrule dates are in correct format
const formatRRuleDates = (rrule) => {
  const formatted = { ...rrule };
  
  // Ensure dtstart and until are ISO strings
  if (formatted.dtstart && !(typeof formatted.dtstart === "string")) {
    formatted.dtstart = new Date(formatted.dtstart).toISOString();
  }
  
  if (formatted.until && !(typeof formatted.until === "string")) {
    formatted.until = new Date(formatted.until).toISOString();
  }
  
  return formatted;
};